/**
 * Data & Analytics vertical — every user-visible string in the sprint that NAMES this domain.
 *
 * Unlike `finance-copy.ts`, nothing here is re-exported from a shipped `_data` layer: this vertical has
 * no prior life as a standalone sprint, so this file IS the source of truth for its copy. The scope rule
 * from `../_engine/types.ts` still holds — if swapping the vertical would change the string, it lives
 * here; invariant chrome ("Back", "Continue", "Module 2 of 4", the swipe stamps) stays in the engine.
 *
 * The register is finance's: plain, concrete, a little opinionated, never markety. Where finance says
 * "income statement", this says "funnel"; where finance's artifact is arithmetic, this one is a
 * sequence of survivors.
 */

import type { LabsCopy } from '../_engine/types';

/**
 * The primer's row operators. Finance's artifact is an income statement, so its operators are real
 * arithmetic (`− COGS`, `= Gross Profit`). A funnel is NOT arithmetic — Signups do not subtract from
 * Visitors, they are a SUBSET of them that survived a step. `↓` says exactly that ("of the row above,
 * this many made it here") and is the only honest operator for this artifact; `=` or `−` would teach a
 * false mental model. The first row has no operator because nothing flows into the top of a funnel.
 */
const PRIMER_OPERATORS: readonly string[] = ['', '↓', '↓', '↓', '↓'];

/** The primer's five rows, in funnel order. Paired to {@link PRIMER_OPERATORS} by index. */
const PRIMER_ROWS: readonly { hint: string; label: string }[] = [
  { hint: 'Everyone who showed up', label: 'Visitors' },
  { hint: 'Interested enough to make an account', label: 'Signups' },
  { hint: 'Actually did the thing once', label: 'Activated Users' },
  { hint: 'Still around three months later', label: 'Retained Users' },
  { hint: 'Willing to pay for it', label: 'Paying Users' },
];

export const DATA_ANALYTICS_COPY: LabsCopy = {
  m1: {
    activity: {
      eyebrow: 'Module 1 · A Day in the Life',
      helper: 'Eight activities from a real data day',
    },
    complete: {
      sub: 'Good start. One module down, three to go.',
    },
    intro: {
      accentEm: 'would I want to do this every day?',
      accentPre: "You'll see {count} real activities, one at a time. For each one, ask yourself:",
      headline: 'A day in the life',
      lead: 'Data careers split into two worlds. Analysts sit with the business and explain what happened and why. Engineers build the systems the answers come out of — the databases, the queries, the tests. The day-to-day work looks very different.',
    },
    results: {
      bridge: {
        emphasis: 'think',
        post: " when the data is messy. That's the other half of your archetype.",
        pre: 'Next: 4 questions about how you ',
      },
      /**
       * Shown INSTEAD of the split reading when the learner swiped right on nothing.
       *
       * Without it, `computeLean` has no signal, returns a dead-even 50/50, and the split copy tells
       * someone who rejected all eight days that "both halves pulled you on different days" above two
       * bars reading 0 and 0. Found by a design review on 2026-08-26.
       */
      noneChosen:
        'You passed on all eight. That is real information, not a failed quiz: those are the days, and none of them appealed. Data may not be your thing, and the useful question now is which kind of work is.',
      interpretation: {
        moderateA:
          'A modest lean toward analytics. The decision side pulled you, but the building side still got a few yeses.',
        moderateB:
          'A modest lean toward engineering. The building pulled you, but you stayed open to the work of explaining it.',
        split:
          "You're genuinely split. Both halves of the stack pulled you on different days. That's a signal, not indecision. The next module sharpens which one fits how you actually think.",
        strongA:
          "A clear lean toward analytics. You're drawn to the decision side: the meeting, the narrative, and the person who has to act on the number.",
        strongB:
          "A clear lean toward engineering. You're drawn to the building side: the query, the schema, and the answer that holds up without you in the room.",
      },
    },
  },
  m2: {
    complete: {
      sub: "Halfway. You're reading products and people now.",
    },
    intro: {
      headline: 'Your mindset, in 4 situations',
      lead: "What you'd do is one signal. How you reason is the other.",
      points: [
        '4 real situations a data person faces',
        'Two reasonable instincts on each. Pick the one that fits you',
        'No right answers, only honest ones',
      ],
    },
    results: {
      footer: 'This will appear on your analysis at the end.',
    },
  },
  m3: {
    activity: {
      diagnosisHeading: 'Diagnose in one sentence',
      diagnosisPlaceholder: "In plain English, what is PulseApp's biggest problem?",
      diagnosisSample:
        'Traffic more than doubled but the signup rate keeps falling, so PulseApp is buying growth at the top of the funnel rather than earning it. Every rate below the top is actually improving.',
      eyebrow: 'Module 3 · Read the funnel',
      feedbackCorrect: 'Nailed it.',
      feedbackWrong: 'Not quite. Try another.',
      metricsHeading: 'Pick the Right Rate for FY2024',
      metricsHelper: 'Three rates. Each has one correct answer. Pick from the options.',
      // The payoff of M3: every rate BELOW the top improves, and the one rate that matters most is
      // quietly falling. Every percentage here is computed off the table in `data-analytics.ts` —
      // signup rate 48/400, 62/560, 82/820; activation 24/48, 32/62, 44/82; paid conversion of
      // activated users 8/24 → 16/44. Changing a cell there means re-deriving this paragraph.
      revealBody:
        'Almost every rate in this funnel is improving. Activation went 50.0% → 51.6% → 53.7%, and the share of activated users who end up paying went 33% → 36%. The team is getting better at converting the people it gets. But look at the top: the signup rate is falling — 12.0% → 11.1% → 10.0% — while visitors more than doubled (+105%). The extra visitors are lower-intent than the ones PulseApp already had. Growth is being bought at the top of the funnel, not earned, and the real product work happening downstream is being masked by the traffic poured in above it. Until the signup rate stabilises, every new visitor is worth less than the last. Working out which END of a funnel is actually broken is exactly what data analysts get paid to do.',
      revealLabel: 'The problem:',
      subhead:
        "A fictional consumer app, a quick warm-up before the real thing. Same funnel structure you'd see for any real product.",
      tableTag: 'Practice example',
    },
    complete: {
      sub: 'One more module, then your real analysis.',
    },
    intro: {
      chips: ['A 30-second primer on how it works', "Then you'll read three rates off a real one"],
      headline: 'Read the funnel',
      subtitle: 'Every data career starts with one artifact: the funnel.',
    },
    primer: {
      // Load-bearing shape: the engine italicises everything after the first ": ", so the thesis must
      // keep its colon — the clause after it is the sentence the learner should remember.
      body: 'A funnel answers one question: of everyone who showed up, who stuck around, and who paid?',
      eyebrow: 'Module 3 · Read the funnel',
      headline: "What's a funnel?",
      pullQuote: 'A funnel is only ever as strong as its weakest step.',
      rows: PRIMER_ROWS.map((row, i) => ({
        hint: row.hint,
        label: row.label,
        operator: PRIMER_OPERATORS[i] ?? '',
      })),
    },
  },
  // The pre-paywall hand-off. Body copy is deliberately near-identical across verticals: what changes
  // between tracks is the artifact the learner just made, not what a six-week externship is.
  handoff: {
    artifactTitle: 'Funnel analysis',
    body: 'That was {duration} of the real thing. The full externship runs six weeks with a real company and a certificate at the end. Your free trial starts it.',
    duration: '15 minutes',
    headline: 'You just finished your first Data & Analytics project',
    trackLabel: 'Data & Analytics track',
  },
  // Mirrors the finance offer's structure — provoke, then promise, then say concretely what 15 minutes
  // buys. "Read a real funnel" is the data equivalent of finance's "read the numbers": the concrete,
  // unglamorous skill the sprint actually hands over.
  offer: {
    body: '15 minutes, start to finish: learn to read a real product funnel, then analyze a company you already use & walk away with work worth sharing',
    eyebrow: 'Data & Analytics Track',
    headlineLead: 'Stop wondering if data is for you.',
    headlineTail: 'Spend 15 minutes finding out.',
    tilePromise: 'Find the story inside a real product funnel',
  },
  prepaywallOffer: {
    body: 'You told us Data & Analytics is your track. This 15-minute exercise is the actual work: read a real product funnel, find the story in it, keep what you make.',
    eyebrow: 'Data & Analytics Track',
    headlineLead: 'Try the real work, free,',
    headlineTail: 'before you pay.',
  },
  m4: {
    activity: {
      feedbackWrong: 'Not the strongest read. Try another.',
      nameHeading: 'Your name for the analysis',
      pickHeading: 'Pick your read',
      pickHelper: "After reading the analysis, pick the read you'd give.",
      sampleFooter: 'Want to go deeper? Push back on this read with your own analysis.',
      sampleHeading: 'Read the sample analysis',
      sampleHelper: 'A curated 3-bullet read on this product — editorial, not a live model. Takes about 8 seconds.',
      samplePanelLabel: 'Sample analysis · curated example',
      sampleRunCta: 'Run analysis',
      submitCta: 'See my analysis',
      takeHeading: 'Where do you agree with the analysis, and where do you see it differently?',
      takeHelper: 'Analysts push back; prompt-followers just paste. Be specific.',
      takePlaceholder: 'In one or two sentences: what did the analysis miss, or what would you push back on?',
    },
    intro: {
      // The LAST paragraph renders as the accent paragraph — order is load-bearing.
      body: [
        "PulseApp was practice. Now you'll pick a product you have almost certainly used, read three years of the user numbers it actually reports to its investors, and form your own view alongside one we generated for you.",
        "The point isn't to agree with it. It's to build your own read and notice exactly where the two diverge. That gap is the job.",
      ],
      headline: 'Your first real product analysis',
    },
    selection: {
      eyebrow: 'Module 4 · Real product analysis',
      headline: 'Pick a product',
      subhead:
        'Each comes with three years of real user numbers, straight from the company. No NDA needed: these companies publish them every quarter.',
    },
  },
  recommendations: {
    archLineNoSubject: "You're {archetype}. Here's where that takes you next.",
    archLineWithSubject: "You're {archetype}. Here's where your {subject} analysis takes you next.",
  },
  welcome: {
    cta: 'Begin',
    // The pill's trailing "· Discovery experience" is invariant chrome (every vertical is one), so it
    // stays in the engine; only the track name is this vertical's to supply.
    eyebrow: 'Data & Analytics Track',
    headlineLead: 'What does a data career actually look like?',
    headlineTail: 'Spend 15 minutes finding out.',
    lead: 'Two real career paths. Your first product analysis.',
    metaDuration: 'About 15 minutes',
    steps: [
      { sub: 'See how two data paths spend a day', title: 'Meet two data people' },
      { sub: 'Quick reads reveal how you think', title: 'Discover your mindset' },
      { sub: 'Read the numbers behind an app you use', title: 'Analyze a real product' },
    ],
  },
};
